/**
 * Konfigurimi i vlerësimit — ciklet dhe shabllonet.
 *
 * Deri tani një vlerësim ishte një rresht i vetëm: periudhë, vit dhe një
 * `result_text` nga pesë vlera të mbyllura. Nuk kishte kritere, nuk kishte
 * peshë, nuk kishte pikë — pra i njëjti formular vlente për agjentin e
 * call-center-it dhe për kontabilistin, dhe askush s'mund të thoshte PSE dikush
 * mori «Mjaftueshëm».
 *
 * Kjo faqe është vendi ku ajo strukturë vendoset. Nuk prek asnjë vlerësim
 * ekzistues: të vjetrit mbeten pa cikël dhe pa shabllon, dhe lexohen si më parë.
 *
 * Asnjë rregull nuk jeton këtu. Shuma e peshave, statuset e lejuara dhe ndalimi
 * i ndryshimit të një cikli të mbyllur vendosen te serveri; kjo faqe i tregon
 * dhe i respekton.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Stack, Typography, Button, Chip, IconButton, Tooltip, Divider,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Alert, CircularProgress, Tabs, Tab, Switch, FormControlLabel,
} from '@mui/material';
import AddIcon        from '@mui/icons-material/Add';
import EditIcon       from '@mui/icons-material/EditOutlined';
import LockIcon       from '@mui/icons-material/LockOutlined';
import DeleteIcon     from '@mui/icons-material/DeleteOutline';
import PageHeader from '../components/common/PageHeader';
import usePermissions from '../hooks/usePermissions';
import { authFetch } from '../services/apiServices';

const C = {
  ink: '#0F172A', ink500: '#64748B', ink400: '#94A3B8',
  line: '#E5E7EB', surface: '#F8FAFC', white: '#FFFFFF',
  accent: '#3E8FA3', good: '#15803D', warn: '#B45309', bad: '#B91C1C',
};

/** Etiketat shqip; kodet rrinë si te baza (CHECK-u i migrimit 082). */
const CYCLE_KINDS = [
  { code: 'monthly',    label: 'Mujor' },
  { code: 'quarterly',  label: 'Tremujor' },
  { code: 'semiannual', label: 'Gjysmëvjetor' },
  { code: 'annual',     label: 'Vjetor' },
  { code: 'probation',  label: 'Provë' },
  { code: 'custom',     label: 'I personalizuar' },
];
const CYCLE_STATUSES = [
  { code: 'draft',     label: 'Draft',        color: C.ink500 },
  { code: 'scheduled', label: 'I planifikuar', color: C.ink500 },
  { code: 'active',    label: 'Aktiv',        color: C.good },
  { code: 'reviewing', label: 'Në rishikim',  color: C.warn },
  { code: 'completed', label: 'I përfunduar', color: C.good },
  { code: 'closed',    label: 'I mbyllur',    color: C.ink400 },
  { code: 'cancelled', label: 'I anuluar',    color: C.bad },
];
const SCOPE_KINDS = [
  { code: 'company',    label: 'Gjithë kompania' },
  { code: 'department', label: 'Departament' },
  { code: 'position',   label: 'Pozitë' },
];

const labelOf = (list, code) => (list.find((x) => x.code === code) || {}).label || code || '—';
const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt)) return d;
  return `${String(dt.getDate()).padStart(2, '0')}.${String(dt.getMonth() + 1).padStart(2, '0')}.${dt.getFullYear()}`;
};

const BLANK_CYCLE = () => ({
  name: '', year: new Date().getFullYear(), kind: 'quarterly', status: 'draft',
  periodStart: '', periodEnd: '', reviewStart: '', reviewDeadline: '',
  templateId: '', selfReview: false,
});
const BLANK_TEMPLATE = () => ({
  name: '', description: '', scopeKind: 'company', scopeId: '', scaleId: '',
  selfReview: false, criteria: [{ name: '', weight: 100, commentRequired: false }],
});

export default function PerformanceSetupPage() {
  const { can } = usePermissions();
  const canManage = can('performance.manage');

  const [tab, setTab]       = useState(0);
  const [cycles, setCycles] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [scales, setScales] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [toast, setToast]     = useState('');

  const [cycleForm, setCycleForm] = useState(null);
  const [tplForm, setTplForm]     = useState(null);
  const [saving, setSaving]       = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const get = async (url) => {
        const r = await authFetch(url);
        const j = await r.json().catch(() => ({}));
        if (!r.ok || j?.success === false) throw new Error(j?.error || `HTTP ${r.status}`);
        return j.data;
      };
      const [c, t, s] = await Promise.all([
        get('/api/performance/cycles'),
        get('/api/performance/templates'),
        get('/api/performance/scales'),
      ]);
      setCycles(c || []); setTemplates(t || []); setScales(s || []);
      // Departamentet dhe pozitat janë opsionale: pa to, shtrirja mbetet
      // «gjithë kompania» dhe faqja vazhdon të punojë.
      get('/api/departments').then((d) => setDepartments(d || [])).catch(() => {});
      get('/api/positions').then((p) => setPositions(p?.data || p || [])).catch(() => {});
    } catch (e) {
      setError(e?.message || 'Konfigurimi nuk u ngarkua.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const send = async (url, method, body) => {
    setSaving(true);
    try {
      const r = await authFetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j?.success === false) throw new Error(j?.error || `HTTP ${r.status}`);
      await load();
      return j.data;
    } finally { setSaving(false); }
  };

  const saveCycle = async () => {
    try {
      const b = { ...cycleForm, templateId: cycleForm.templateId || null };
      await send(cycleForm.id ? `/api/performance/cycles/${cycleForm.id}` : '/api/performance/cycles',
        cycleForm.id ? 'PUT' : 'POST', b);
      setCycleForm(null); setToast('Cikli u ruajt.');
    } catch (e) { setToast(e?.message || 'Ruajtja dështoi.'); }
  };

  const closeCycle = async (c) => {
    try {
      const d = await send(`/api/performance/cycles/${c.id}/close`, 'POST', {});
      setToast(`Cikli u mbyll. U ngrinë ${d?.lockedEvaluations ?? 0} vlerësime.`);
    } catch (e) { setToast(e?.message || 'Mbyllja dështoi.'); }
  };

  const saveTemplate = async () => {
    try {
      const b = { ...tplForm, scaleId: tplForm.scaleId || null, scopeId: tplForm.scopeId || null };
      await send(tplForm.id ? `/api/performance/templates/${tplForm.id}` : '/api/performance/templates',
        tplForm.id ? 'PUT' : 'POST', b);
      setTplForm(null); setToast('Shablloni u ruajt.');
    } catch (e) { setToast(e?.message || 'Ruajtja dështoi.'); }
  };

  /** Shuma e peshave — tregohet ndërsa shkruhet, jo vetëm pas refuzimit. */
  const weightTotal = useMemo(
    () => (tplForm?.criteria || []).reduce((s, c) => s + (Number(c.weight) || 0), 0),
    [tplForm],
  );
  const weightOk = Math.abs(weightTotal - 100) < 0.01;

  const scopeOptions = tplForm?.scopeKind === 'department' ? departments
    : tplForm?.scopeKind === 'position' ? positions : [];

  return (
    <Box>
      <PageHeader
        title="Konfigurimi i vlerësimit"
        subtitle="Ciklet e vlerësimit dhe shabllonet me kritere e peshë. Vlerësimet ekzistuese nuk preken."
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {toast && <Alert severity="info" onClose={() => setToast('')} sx={{ mb: 2 }}>{toast}</Alert>}
      {!canManage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Ke qasje vetëm për lexim. Krijimi dhe ndryshimi i cikleve kërkojnë të drejtën «performance.manage».
        </Alert>
      )}

      <Paper elevation={0} sx={{ border: `1px solid ${C.line}`, borderRadius: 2, overflow: 'hidden' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          sx={{ px: 1.5, bgcolor: C.surface, borderBottom: `1px solid ${C.line}`, minHeight: 46,
                '& .MuiTab-root': { textTransform: 'none', fontWeight: 650, minHeight: 46 } }}>
          <Tab label={`Ciklet (${cycles.length})`} />
          <Tab label={`Shabllonet (${templates.length})`} />
        </Tabs>

        {loading ? (
          <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress size={24} /></Stack>
        ) : tab === 0 ? (
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 1.5 }}>
              <Typography sx={{ fontSize: 13, color: C.ink500 }}>
                Periudha e vlerësimit me afatet e veta. Mbyllja e një cikli i ngrin rezultatet e tij.
              </Typography>
              {canManage && (
                <Button size="small" variant="contained" startIcon={<AddIcon />}
                  onClick={() => setCycleForm(BLANK_CYCLE())}
                  sx={{ textTransform: 'none', fontWeight: 650, borderRadius: 1.5 }}>
                  Cikël i ri
                </Button>
              )}
            </Stack>
            <Divider />
            {cycles.length === 0 ? (
              <Typography sx={{ py: 5, textAlign: 'center', color: C.ink500, fontSize: '.88rem' }}>
                Ende asnjë cikël vlerësimi. Vlerësimet ekzistuese vazhdojnë të funksionojnë pa të.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: C.surface }}>
                      <TableCell sx={{ fontWeight: 700 }}>Cikli</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Lloji</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Periudha</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Afati i rishikimit</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Shablloni</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Vlerësime</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Statusi</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Veprime</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cycles.map((c) => {
                      const st = CYCLE_STATUSES.find((x) => x.code === c.status) || {};
                      const locked = c.status === 'closed';
                      return (
                        <TableRow key={c.id} hover>
                          <TableCell>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{c.name}</Typography>
                            <Typography sx={{ fontSize: 11.5, color: C.ink500 }}>{c.year}</Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: 13 }}>{labelOf(CYCLE_KINDS, c.kind)}</TableCell>
                          <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                            {fmtDate(c.period_start)} – {fmtDate(c.period_end)}
                          </TableCell>
                          <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{fmtDate(c.review_deadline)}</TableCell>
                          <TableCell sx={{ fontSize: 13 }}>{c.template_name || '—'}</TableCell>
                          <TableCell sx={{ fontSize: 13 }}>{c.evaluation_count}</TableCell>
                          <TableCell>
                            <Chip size="small" label={labelOf(CYCLE_STATUSES, c.status)}
                              sx={{ height: 22, fontWeight: 700, fontSize: '.7rem', color: st.color, bgcolor: `${st.color}18` }} />
                          </TableCell>
                          <TableCell align="right">
                            {canManage && !locked && (
                              <>
                                <Tooltip title="Ndrysho" arrow>
                                  <IconButton size="small" onClick={() => setCycleForm({
                                    id: c.id, name: c.name, year: c.year, kind: c.kind, status: c.status,
                                    periodStart: c.period_start?.slice(0, 10) || '',
                                    periodEnd: c.period_end?.slice(0, 10) || '',
                                    reviewStart: c.review_start?.slice(0, 10) || '',
                                    reviewDeadline: c.review_deadline?.slice(0, 10) || '',
                                    templateId: c.template_id || '', selfReview: c.self_review,
                                  })}><EditIcon sx={{ fontSize: 17 }} /></IconButton>
                                </Tooltip>
                                <Tooltip title="Mbyll ciklin — rezultatet ngrijnë" arrow>
                                  <IconButton size="small" onClick={() => closeCycle(c)}>
                                    <LockIcon sx={{ fontSize: 17 }} />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                            {locked && (
                              <Tooltip title={`I mbyllur më ${fmtDate(c.closed_at)} — rezultatet nuk ndryshohen më`} arrow>
                                <LockIcon sx={{ fontSize: 17, color: C.ink400 }} />
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        ) : (
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 1.5 }}>
              <Typography sx={{ fontSize: 13, color: C.ink500 }}>
                Kriteret dhe pesha e tyre. Më i ngushti vlen: pozita para departamentit, departamenti para kompanisë.
              </Typography>
              {canManage && (
                <Button size="small" variant="contained" startIcon={<AddIcon />}
                  onClick={() => setTplForm(BLANK_TEMPLATE())}
                  sx={{ textTransform: 'none', fontWeight: 650, borderRadius: 1.5 }}>
                  Shabllon i ri
                </Button>
              )}
            </Stack>
            <Divider />
            {templates.length === 0 ? (
              <Typography sx={{ py: 5, textAlign: 'center', color: C.ink500, fontSize: '.88rem' }}>
                Ende asnjë shabllon. Pa të, vlerësimi mbetet si deri tani: një rezultat i vetëm pa kritere.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: C.surface }}>
                      <TableCell sx={{ fontWeight: 700 }}>Shablloni</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Shtrirja</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Kriteret</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Vetëvlerësim</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Veprime</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {templates.map((t) => (
                      <TableRow key={t.id} hover sx={t.archived ? { opacity: 0.55 } : undefined}>
                        <TableCell>
                          <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{t.name}</Typography>
                          {t.description && <Typography sx={{ fontSize: 11.5, color: C.ink500 }}>{t.description}</Typography>}
                        </TableCell>
                        <TableCell sx={{ fontSize: 13 }}>{labelOf(SCOPE_KINDS, t.scope_kind)}</TableCell>
                        <TableCell sx={{ fontSize: 13 }}>
                          {(t.criteria || []).map((c) => `${c.name} ${Number(c.weight)}%`).join(' · ') || '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: 13 }}>{t.self_review ? 'Po' : 'Jo'}</TableCell>
                        <TableCell align="right">
                          {canManage && (
                            <Tooltip title="Ndrysho" arrow>
                              <IconButton size="small" onClick={() => setTplForm({
                                id: t.id, name: t.name, description: t.description || '',
                                scopeKind: t.scope_kind, scopeId: t.scope_id || '',
                                scaleId: t.scale_id || '', selfReview: t.self_review, archived: t.archived,
                                criteria: (t.criteria || []).map((c) => ({
                                  name: c.name, description: c.description || '',
                                  weight: Number(c.weight), commentRequired: c.comment_required,
                                })),
                              })}><EditIcon sx={{ fontSize: 17 }} /></IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Paper>

      {/* ── Dialogu i ciklit ── */}
      <Dialog open={Boolean(cycleForm)} onClose={() => setCycleForm(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{cycleForm?.id ? 'Ndrysho ciklin' : 'Cikël i ri'}</DialogTitle>
        <DialogContent>
          {cycleForm && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Emri" value={cycleForm.name} required fullWidth
                placeholder="p.sh. Gjysmëvjetori 1 2026"
                onChange={(e) => setCycleForm({ ...cycleForm, name: e.target.value })} />
              <Stack direction="row" spacing={2}>
                <TextField label="Viti" type="number" value={cycleForm.year} fullWidth
                  onChange={(e) => setCycleForm({ ...cycleForm, year: e.target.value })} />
                <TextField select label="Lloji" value={cycleForm.kind} fullWidth
                  onChange={(e) => setCycleForm({ ...cycleForm, kind: e.target.value })}>
                  {CYCLE_KINDS.map((k) => <MenuItem key={k.code} value={k.code}>{k.label}</MenuItem>)}
                </TextField>
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField label="Periudha nga" type="date" InputLabelProps={{ shrink: true }} fullWidth
                  value={cycleForm.periodStart} onChange={(e) => setCycleForm({ ...cycleForm, periodStart: e.target.value })} />
                <TextField label="Periudha deri" type="date" InputLabelProps={{ shrink: true }} fullWidth
                  value={cycleForm.periodEnd} onChange={(e) => setCycleForm({ ...cycleForm, periodEnd: e.target.value })} />
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField label="Rishikimi nga" type="date" InputLabelProps={{ shrink: true }} fullWidth
                  value={cycleForm.reviewStart} onChange={(e) => setCycleForm({ ...cycleForm, reviewStart: e.target.value })} />
                <TextField label="Afati i rishikimit" type="date" InputLabelProps={{ shrink: true }} fullWidth
                  value={cycleForm.reviewDeadline} onChange={(e) => setCycleForm({ ...cycleForm, reviewDeadline: e.target.value })} />
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField select label="Statusi" value={cycleForm.status} fullWidth
                  onChange={(e) => setCycleForm({ ...cycleForm, status: e.target.value })}>
                  {CYCLE_STATUSES.filter((s) => s.code !== 'closed').map((s) => (
                    <MenuItem key={s.code} value={s.code}>{s.label}</MenuItem>
                  ))}
                </TextField>
                <TextField select label="Shablloni" value={cycleForm.templateId} fullWidth
                  onChange={(e) => setCycleForm({ ...cycleForm, templateId: e.target.value })}>
                  <MenuItem value="">(pa shabllon)</MenuItem>
                  {templates.filter((t) => !t.archived).map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                </TextField>
              </Stack>
              <FormControlLabel
                control={<Switch checked={cycleForm.selfReview} size="small"
                  onChange={(e) => setCycleForm({ ...cycleForm, selfReview: e.target.checked })} />}
                label="Punonjësi e plotëson vetëvlerësimin i pari" />
              {/* «I mbyllur» mungon te lista me qëllim: mbyllja bëhet me butonin
                  e vet, sepse ngrin rezultatet dhe nuk kthehet mbrapsht. */}
              <Alert severity="info" sx={{ py: 0.25, fontSize: '.8rem' }}>
                Mbyllja e ciklit bëhet nga lista, me bravën. Ajo i ngrin vlerësimet e tij përgjithmonë.
              </Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setCycleForm(null)} color="inherit">Anulo</Button>
          <Button variant="contained" onClick={saveCycle} disabled={saving || !cycleForm?.name?.trim()}>
            {saving ? 'Duke ruajtur…' : 'Ruaj'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialogu i shabllonit ── */}
      <Dialog open={Boolean(tplForm)} onClose={() => setTplForm(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{tplForm?.id ? 'Ndrysho shabllonin' : 'Shabllon i ri'}</DialogTitle>
        <DialogContent>
          {tplForm && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Emri" value={tplForm.name} required fullWidth
                placeholder="p.sh. Customer Support"
                onChange={(e) => setTplForm({ ...tplForm, name: e.target.value })} />
              <TextField label="Përshkrimi" value={tplForm.description} fullWidth
                onChange={(e) => setTplForm({ ...tplForm, description: e.target.value })} />
              <Stack direction="row" spacing={2}>
                <TextField select label="Shtrirja" value={tplForm.scopeKind} fullWidth
                  onChange={(e) => setTplForm({ ...tplForm, scopeKind: e.target.value, scopeId: '' })}>
                  {SCOPE_KINDS.map((s) => <MenuItem key={s.code} value={s.code}>{s.label}</MenuItem>)}
                </TextField>
                {tplForm.scopeKind !== 'company' && (
                  <TextField select label={tplForm.scopeKind === 'department' ? 'Departamenti' : 'Pozita'}
                    value={tplForm.scopeId} fullWidth
                    onChange={(e) => setTplForm({ ...tplForm, scopeId: e.target.value })}>
                    {scopeOptions.map((o) => (
                      <MenuItem key={o.Id ?? o.id} value={o.Id ?? o.id}>{o.Name ?? o.name}</MenuItem>
                    ))}
                  </TextField>
                )}
                <TextField select label="Shkalla" value={tplForm.scaleId} fullWidth
                  onChange={(e) => setTplForm({ ...tplForm, scaleId: e.target.value })}>
                  <MenuItem value="">(parazgjedhja)</MenuItem>
                  {scales.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                </TextField>
              </Stack>

              <Divider />
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Kriteret</Typography>
                {/* Shuma tregohet ndërsa shkruhet: përndryshe gabimi del vetëm
                    pas «Ruaj», kur tërë formulari është plotësuar tashmë. */}
                <Chip size="small" label={`Shuma: ${weightTotal.toFixed(0)}%`}
                  sx={{ fontWeight: 700, bgcolor: weightOk ? '#DCFCE7' : '#FEF3C7',
                        color: weightOk ? C.good : C.warn }} />
              </Stack>
              {tplForm.criteria.map((c, i) => (
                <Stack key={i} direction="row" spacing={1} alignItems="center">
                  <TextField label={`Kriteri ${i + 1}`} value={c.name} fullWidth size="small"
                    onChange={(e) => {
                      const next = [...tplForm.criteria]; next[i] = { ...c, name: e.target.value };
                      setTplForm({ ...tplForm, criteria: next });
                    }} />
                  <TextField label="Pesha %" type="number" value={c.weight} size="small" sx={{ width: 120 }}
                    onChange={(e) => {
                      const next = [...tplForm.criteria]; next[i] = { ...c, weight: e.target.value };
                      setTplForm({ ...tplForm, criteria: next });
                    }} />
                  <IconButton size="small" disabled={tplForm.criteria.length === 1}
                    onClick={() => setTplForm({ ...tplForm, criteria: tplForm.criteria.filter((_, j) => j !== i) })}>
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Stack>
              ))}
              <Button size="small" startIcon={<AddIcon />} sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                onClick={() => setTplForm({ ...tplForm, criteria: [...tplForm.criteria, { name: '', weight: 0, commentRequired: false }] })}>
                Shto kriter
              </Button>
              {!weightOk && (
                <Alert severity="warning" sx={{ py: 0.25, fontSize: '.8rem' }}>
                  Shuma duhet të jetë 100%. Me {weightTotal.toFixed(0)}%, maksimumi i mundshëm do të ishte
                  {' '}{weightTotal.toFixed(0)} pikë — dhe çdo punonjës do të dukej nën pritshmëri pa faj.
                </Alert>
              )}
              <FormControlLabel
                control={<Switch checked={tplForm.selfReview} size="small"
                  onChange={(e) => setTplForm({ ...tplForm, selfReview: e.target.checked })} />}
                label="Kërko vetëvlerësim me këtë shabllon" />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setTplForm(null)} color="inherit">Anulo</Button>
          <Button variant="contained" onClick={saveTemplate}
            disabled={saving || !tplForm?.name?.trim() || !weightOk}>
            {saving ? 'Duke ruajtur…' : 'Ruaj'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
